(function () {
  'use strict';

  var COMPONENT_NAME = 'vl-product-extra-purchase-option';
  var DATA_SELECTOR = '[data-vl-product-extra-purchase-option-data]';
  var OPTION_INPUTS_SELECTOR = '[data-variant-picker-option-inputs]';
  var OPTION_LABELS_SELECTOR = '[data-variant-picker-option-labels]';
  var EXTENSION_BUTTON_SELECTOR = '[data-vl-extra-purchase-product-id]';
  var APPLY_DELAYS = [0, 120, 450, 900];
  var AFFIRM_UPDATE_DELAY = 400;

  function normalize(value) {
    return String(value == null ? '' : value)
      .toLocaleLowerCase()
      .replace(/\s+/g, '');
  }

  function getOptionNameAliases(value) {
    var original = String(value == null ? '' : value).trim();
    var aliases = [normalize(original)];
    var parenthesized = original.match(/[\(（]([^\)）]+)[\)）]\s*$/);
    if (parenthesized && parenthesized[1]) {
      aliases.push(normalize(parenthesized[1]));
    }
    return Array.from(new Set(aliases.filter(Boolean)));
  }

  function escapeSelector(value) {
    if (window.CSS && typeof window.CSS.escape === 'function') {
      return window.CSS.escape(String(value));
    }
    return String(value).replace(/["\\]/g, '\\$&');
  }

  function parseJson(script) {
    if (!script) return null;
    try {
      return JSON.parse(script.textContent || '{}');
    } catch (error) {
      console.warn('[VL product extra purchase option] Invalid component data.', error);
      return null;
    }
  }

  function toNumber(value, fallback) {
    var number = Number(value);
    return Number.isFinite(number) ? number : fallback;
  }

  function formatWithDelimiters(cents, precision, thousands, decimal) {
    var value = (toNumber(cents, 0) / 100).toFixed(precision);
    var parts = value.split('.');
    parts[0] = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1' + thousands);
    return parts[1] ? parts[0] + decimal + parts[1] : parts[0];
  }

  function formatMoney(cents, format) {
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      return window.Shopify.formatMoney(cents, format);
    }

    var moneyFormat = format || '${{amount}}';
    var match = moneyFormat.match(/\{\{\s*(\w+)\s*\}\}/);
    if (!match) return '$' + formatWithDelimiters(cents, 2, ',', '.');

    var token = match[1];
    var formatted = {
      amount: formatWithDelimiters(cents, 2, ',', '.'),
      amount_no_decimals: formatWithDelimiters(cents, 0, ',', '.'),
      amount_with_comma_separator: formatWithDelimiters(cents, 2, '.', ','),
      amount_no_decimals_with_comma_separator: formatWithDelimiters(cents, 0, '.', ','),
      amount_with_apostrophe_separator: formatWithDelimiters(cents, 2, "'", '.'),
    }[token];

    return moneyFormat.replace(match[0], formatted || formatWithDelimiters(cents, 2, ',', '.'));
  }

  function htmlToText(html) {
    var template = document.createElement('template');
    template.innerHTML = html;
    return template.content.textContent || '';
  }

  function formatCompactMoney(cents, format) {
    var amount = toNumber(cents, 0);
    var formatted = htmlToText(formatMoney(amount, format));

    if (amount % 100 === 0) {
      return formatted.replace(/([.,])00(?=\D*$)/, '');
    }
    if (amount % 10 === 0) {
      return formatted.replace(/([.,]\d)0(?=\D*$)/, '$1');
    }
    return formatted;
  }

  class VlProductExtraPurchaseOption extends HTMLElement {
    constructor() {
      super();
      this.data = null;
      this.variantPicker = null;
      this.productRoot = null;
      this.productForm = null;
      this.addToCartForm = null;
      this.records = [];
      this.groups = new Map();
      this.buttons = new Map();
      this.nativeTargetLabels = new Set();
      this.currentPurchase = null;
      this.applyTimers = [];
      this.cartSyncTimers = [];
      this.gallerySnapshots = new Map();
      this.appliedGalleryKey = null;
      this.dispatchedPurchaseVariantId = null;
      this.affirmUpdateTimer = null;
      this.pendingAffirmPrice = null;
      this.observer = null;
      this.observerTarget = null;
      this.disconnectTimer = null;
      this.isApplying = false;
      this.isInitialized = false;
      this.handlePickerChange = this.handlePickerChange.bind(this);
      this.handleExtensionClick = this.handleExtensionClick.bind(this);
      this.handleExtensionKeydown = this.handleExtensionKeydown.bind(this);
      this.handleNativeTargetClick = this.handleNativeTargetClick.bind(this);
      this.handleNativeTargetKeydown = this.handleNativeTargetKeydown.bind(this);
      this.handleProductFormSubmit = this.handleProductFormSubmit.bind(this);
      this.handleMutations = this.handleMutations.bind(this);
    }

    connectedCallback() {
      if (this.disconnectTimer) {
        window.clearTimeout(this.disconnectTimer);
        this.disconnectTimer = null;
      }
      if (this.isInitialized) return;
      this.initialize();
    }

    disconnectedCallback() {
      this.disconnectTimer = window.setTimeout(function () {
        this.disconnectTimer = null;
        if (this.isConnected) return;
        this.destroy();
      }.bind(this), 0);
    }

    destroy() {
      if (this.currentPurchase && this.productRoot && this.productRoot.isConnected) {
        this.switchToTargetProduct();
      }
      this.clearApplyTimers();
      this.clearCartSyncTimers();
      this.clearAffirmUpdateTimer();
      this.stopObserver();
      if (this.variantPicker) {
        this.variantPicker.removeEventListener('change', this.handlePickerChange);
      }
      if (this.addToCartForm) {
        this.addToCartForm.removeEventListener('submit', this.handleProductFormSubmit, true);
      }
      this.buttons.forEach(function (button) {
        button.removeEventListener('click', this.handleExtensionClick);
        button.removeEventListener('keydown', this.handleExtensionKeydown);
      }, this);
      this.nativeTargetLabels.forEach(function (label) {
        label.removeEventListener('click', this.handleNativeTargetClick);
        label.removeEventListener('keydown', this.handleNativeTargetKeydown);
        delete label.dataset.vlExtraPurchaseTargetOption;
      }, this);
      this.buttons.forEach(function (button) { button.remove(); });
      this.records = [];
      this.groups.clear();
      this.buttons.clear();
      this.nativeTargetLabels.clear();
      this.variantPicker = null;
      this.productForm = null;
      this.addToCartForm = null;
      this.productRoot = null;
      this.currentPurchase = null;
      this.appliedGalleryKey = null;
      this.dispatchedPurchaseVariantId = null;
      this.isInitialized = false;
    }

    async initialize() {
      this.data = parseJson(this.querySelector(DATA_SELECTOR));
      if (!this.data || !Array.isArray(this.data.records) || this.data.records.length === 0) return;

      await customElements.whenDefined('variant-picker');
      this.variantPicker = document.getElementById(this.dataset.variantPickerId);
      this.productForm = document.getElementById(this.dataset.productFormId);
      this.productRoot = this.closest('product-content') || this.closest('.shopify-section') || document;

      if (!this.variantPicker || !this.productForm) return;

      this.records = this.mergeRecords(this.data.records);
      this.renderExtensionOptions();
      if (this.buttons.size === 0) return;

      this.variantPicker.addEventListener('change', this.handlePickerChange);
      this.addToCartForm = this.productForm.querySelector('form');
      if (this.addToCartForm) {
        this.addToCartForm.addEventListener('submit', this.handleProductFormSubmit, true);
      }
      this.startObserver();
      this.refreshExtensionAvailability();
      this.isInitialized = true;
      this.selectDefaultPurchaseProduct();
    }

    mergeRecords(records) {
      var groups = new Map();

      records.forEach(function (record) {
        var targetOption = String(record.targetOption || '').trim();
        var targetOptionNormalized = normalize(targetOption);
        if (!targetOptionNormalized || !Array.isArray(record.products)) return;

        if (!groups.has(targetOptionNormalized)) {
          groups.set(targetOptionNormalized, {
            targetOption: targetOption,
            targetOptionNormalized: targetOptionNormalized,
            products: [],
            productIds: new Set(),
          });
        }

        var group = groups.get(targetOptionNormalized);
        record.products.forEach(function (product) {
          var productId = String(product.id);
          if (!productId) return;
          if (group.productIds.has(productId)) {
            var existingProduct = group.products.find(function (item) {
              return String(item.id) === productId;
            });
            if (existingProduct && record.isPurchaseFirst === true) {
              existingProduct.isPurchaseFirst = true;
            }
            return;
          }
          group.productIds.add(productId);
          product.isPurchaseFirst = record.isPurchaseFirst === true;
          product.optionIndex = new Map();
          (product.options || []).forEach(function (option, index) {
            getOptionNameAliases(option.name).forEach(function (alias) {
              product.optionIndex.set(alias, index);
            });
          });
          group.products.push(product);
        });
      });

      groups.forEach(function (group) {
        group.products = group.products.filter(function (product) {
          return product.isPurchaseFirst === true;
        }).concat(group.products.filter(function (product) {
          return product.isPurchaseFirst !== true;
        }));
      });

      return Array.from(groups.values());
    }

    getPickerStructure() {
      var inputContainers = Array.from(this.variantPicker.querySelectorAll(OPTION_INPUTS_SELECTOR));
      var labelContainers = Array.from(this.variantPicker.querySelectorAll(OPTION_LABELS_SELECTOR));

      return labelContainers.map(function (labelContainer, index) {
        var optionName = labelContainer.getAttribute('option-picker-name') || '';
        var inputContainer = inputContainers[index];
        return {
          name: optionName,
          normalizedName: normalize(optionName),
          labelContainer: labelContainer,
          inputContainer: inputContainer,
          inputs: inputContainer ? Array.from(inputContainer.querySelectorAll('input[type="radio"]')) : [],
        };
      });
    }

    getSelectedTargetOptions() {
      var selected = new Map();
      this.getPickerStructure().forEach(function (option) {
        var checked = option.inputs.find(function (input) { return input.checked; });
        if (checked) selected.set(option.normalizedName, checked.value);
      });
      return selected;
    }

    getGroupByOptionName(normalizedName) {
      return this.getPickerStructure().find(function (option) {
        return option.normalizedName === normalizedName;
      });
    }

    getLabelsForInput(input) {
      if (!input || !input.id) return [];
      var selector = 'label[for="' + escapeSelector(input.id) + '"]';
      return Array.from((this.productRoot || document).querySelectorAll(selector));
    }

    getOrCreateExtensionHost(optionGroup) {
      var existingHost = optionGroup.labelContainer.querySelector('[data-vl-extra-purchase-option-host]');
      if (existingHost) return existingHost;

      var nativePillList = optionGroup.labelContainer.querySelector('.product-variant-picker__pill-list');
      if (nativePillList) {
        nativePillList.setAttribute('data-vl-extra-purchase-option-host', '');
        return nativePillList;
      }

      var host = document.createElement('div');
      host.className = 'product-variant-picker__pill-list vl-product-extra-purchase-option__list';
      host.setAttribute('data-vl-extra-purchase-option-host', '');
      host.setAttribute('role', 'radiogroup');
      host.setAttribute('aria-label', optionGroup.name);
      optionGroup.labelContainer.appendChild(host);
      return host;
    }

    renderExtensionOptions() {
      var structure = this.getPickerStructure();

      this.records.forEach(function (record) {
        var optionGroup = structure.find(function (option) {
          return option.normalizedName === record.targetOptionNormalized;
        });
        if (!optionGroup) return;

        this.groups.set(record.targetOptionNormalized, optionGroup);
        var host = this.getOrCreateExtensionHost(optionGroup);

        optionGroup.inputs.forEach(function (input) {
          this.getLabelsForInput(input).forEach(function (label) {
            if (this.nativeTargetLabels.has(label)) return;
            label.dataset.vlExtraPurchaseTargetOption = record.targetOptionNormalized;
            label.addEventListener('click', this.handleNativeTargetClick);
            label.addEventListener('keydown', this.handleNativeTargetKeydown);
            this.nativeTargetLabels.add(label);
          }, this);
        }, this);

        record.products.forEach(function (product) {
          var key = record.targetOptionNormalized + ':' + product.id;
          if (this.buttons.has(key)) return;

          var button = document.createElement('button');
          button.type = 'button';
          button.className = 'pill pill--large pill--outline vl-product-extra-purchase-option__item';
          button.setAttribute('role', 'radio');
          button.setAttribute('aria-checked', 'false');
          button.dataset.vlExtraPurchaseProductId = String(product.id);
          button.dataset.vlExtraPurchaseTargetOption = record.targetOptionNormalized;
          button.innerHTML = '<span class="pill__label"></span>';
          button.querySelector('.pill__label').textContent = product.title;
          button.addEventListener('click', this.handleExtensionClick);
          button.addEventListener('keydown', this.handleExtensionKeydown);
          if (product.isPurchaseFirst === true) {
            var firstNativeLabel = host.querySelector('label:not([data-vl-extra-purchase-product-id])');
            host.insertBefore(button, firstNativeLabel || host.firstChild);
          } else {
            host.appendChild(button);
          }
          this.buttons.set(key, button);
        }, this);
      }, this);
    }

    selectDefaultPurchaseProduct() {
      for (var recordIndex = 0; recordIndex < this.records.length; recordIndex += 1) {
        var record = this.records[recordIndex];
        for (var productIndex = 0; productIndex < record.products.length; productIndex += 1) {
          var product = record.products[productIndex];
          if (product.isPurchaseFirst !== true) continue;

          var matches = this.findMatchingVariants(
            product,
            record.targetOptionNormalized,
            this.getSelectedTargetOptions()
          );
          if (!matches.some(function (variant) { return variant.available; })) continue;

          this.selectPurchaseProduct(record.targetOptionNormalized, product.id);
          return true;
        }
      }
      return false;
    }

    findRecordAndProduct(targetOptionNormalized, productId) {
      var record = this.records.find(function (item) {
        return item.targetOptionNormalized === targetOptionNormalized;
      });
      if (!record) return null;
      var product = record.products.find(function (item) {
        return String(item.id) === String(productId);
      });
      return product ? { record: record, product: product } : null;
    }

    findMatchingVariants(product, targetOptionNormalized, selectedOptions, overrides) {
      var overrideValues = overrides || new Map();
      return (product.variants || []).filter(function (variant) {
        if (variant.hidden === true) return false;
        return Array.from(product.optionIndex.entries()).every(function (entry) {
          var optionName = entry[0];
          var optionIndex = entry[1];
          if (optionName === targetOptionNormalized) return true;

          var expected = overrideValues.has(optionName)
            ? overrideValues.get(optionName)
            : selectedOptions.get(optionName);
          if (expected == null) return true;
          return normalize(variant.options[optionIndex]) === normalize(expected);
        });
      });
    }

    refreshExtensionAvailability() {
      var selectedOptions = this.getSelectedTargetOptions();

      this.records.forEach(function (record) {
        record.products.forEach(function (product) {
          var key = record.targetOptionNormalized + ':' + product.id;
          var button = this.buttons.get(key);
          if (!button) return;

          var matches = this.findMatchingVariants(
            product,
            record.targetOptionNormalized,
            selectedOptions
          );
          var hasAvailable = matches.some(function (variant) { return variant.available; });
          var isActive = this.currentPurchase
            && this.currentPurchase.targetOptionNormalized === record.targetOptionNormalized
            && String(this.currentPurchase.product.id) === String(product.id);

          button.hidden = matches.length === 0 && !isActive;
          button.classList.toggle('vl-product-extra-purchase-option__item--sold-out', matches.length > 0 && !hasAvailable);
          button.toggleAttribute('no-available', matches.length > 0 && !hasAvailable);
          button.toggleAttribute('out-of-stock', matches.length > 0 && !hasAvailable);
          button.setAttribute('aria-disabled', matches.length > 0 && !hasAvailable ? 'true' : 'false');
          button.disabled = matches.length > 0 && !hasAvailable && !isActive;
        }, this);
      }, this);
    }

    handleExtensionClick(event) {
      var button = event.currentTarget;
      if (button.disabled || button.getAttribute('aria-disabled') === 'true') return;
      this.selectPurchaseProduct(
        button.dataset.vlExtraPurchaseTargetOption,
        button.dataset.vlExtraPurchaseProductId
      );
    }

    handleExtensionKeydown(event) {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      event.preventDefault();
      this.handleExtensionClick(event);
    }

    handleNativeTargetClick(event) {
      var label = event.currentTarget;
      if (
        this.currentPurchase
        && label.dataset.vlExtraPurchaseTargetOption === this.currentPurchase.targetOptionNormalized
      ) {
        this.switchToTargetProduct();
      }
    }

    handleNativeTargetKeydown(event) {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      this.handleNativeTargetClick(event);
    }

    selectPurchaseProduct(targetOptionNormalized, productId) {
      var result = this.findRecordAndProduct(targetOptionNormalized, productId);
      if (!result) return;

      var selectedOptions = this.getSelectedTargetOptions();
      var matches = this.findMatchingVariants(
        result.product,
        targetOptionNormalized,
        selectedOptions
      );
      var variant = matches.find(function (item) { return item.available; }) || matches[0];
      if (!variant || !variant.available) return;

      this.currentPurchase = {
        targetOptionNormalized: targetOptionNormalized,
        targetOption: result.record.targetOption,
        product: result.product,
        variant: variant,
      };

      this.applyPurchaseState();
      this.dispatchPurchaseSubjectChange();
    }

    handlePickerChange(event) {
      var input = event.target.closest && event.target.closest('input[type="radio"]');
      if (!input || !this.variantPicker.contains(input)) return;

      var optionGroup = this.getPickerStructure().find(function (option) {
        return option.inputContainer && option.inputContainer.contains(input);
      });

      if (
        this.currentPurchase
        && optionGroup
        && optionGroup.normalizedName === this.currentPurchase.targetOptionNormalized
      ) {
        this.switchToTargetProduct();
        return;
      }

      if (this.currentPurchase) {
        this.schedulePurchaseState();
      } else {
        window.setTimeout(function () {
          this.refreshExtensionAvailability();
        }.bind(this), 0);
      }
    }

    schedulePurchaseState() {
      this.clearApplyTimers();
      APPLY_DELAYS.forEach(function (delay) {
        var timer = window.setTimeout(function () {
          if (this.currentPurchase) this.applyPurchaseState();
        }.bind(this), delay);
        this.applyTimers.push(timer);
      }, this);
    }

    scheduleTargetGalleryRestore() {
      APPLY_DELAYS.forEach(function (delay) {
        var timer = window.setTimeout(function () {
          if (this.currentPurchase || !this.isConnected) return;
          var productContent = this.getProductContent();
          this.restoreProductContentGallery(productContent);
          this.restoreTargetPurchasePresentation();
        }.bind(this), delay);
        this.applyTimers.push(timer);
      }, this);
    }

    clearApplyTimers() {
      this.applyTimers.forEach(function (timer) { window.clearTimeout(timer); });
      this.applyTimers = [];
    }

    handleProductFormSubmit() {
      if (!this.currentPurchase) return;
      this.suppressTargetProductExtrasForSubmit();
      this.clearCartSyncTimers();
      [700, 1600, 3000].forEach(function (delay) {
        var timer = window.setTimeout(function () {
          this.syncCartCount();
        }.bind(this), delay);
        this.cartSyncTimers.push(timer);
      }, this);
    }

    suppressTargetProductExtrasForSubmit() {
      var freeItemHosts = Array.from(this.productRoot.querySelectorAll('.product-card-2-form[data-freeitem]'));
      // Free Essentials belongs to the target (bike) product and must not be
      // submitted when an extra-purchase product is active. Add 3 accessories
      // are independent upsells and should remain checked for either product.
      var checkedFreeItems = Array.from(
        this.productRoot.querySelectorAll('.bundle-container #free .checkbox-input.acc-checkbox:checked')
      );
      var freeItemValues = freeItemHosts.map(function (host) {
        return host.getAttribute('data-freeitem');
      });

      freeItemHosts.forEach(function (host) {
        host.setAttribute('data-freeitem', '');
      });
      checkedFreeItems.forEach(function (input) {
        input.checked = false;
      });

      window.setTimeout(function () {
        freeItemHosts.forEach(function (host, index) {
          var value = freeItemValues[index];
          if (value == null) host.removeAttribute('data-freeitem');
          else host.setAttribute('data-freeitem', value);
        });
        checkedFreeItems.forEach(function (input) {
          input.checked = true;
        });
      }, 0);
    }

    clearCartSyncTimers() {
      this.cartSyncTimers.forEach(function (timer) { window.clearTimeout(timer); });
      this.cartSyncTimers = [];
    }

    async syncCartCount() {
      try {
        var rootUrl = window.Shopify && window.Shopify.routes && window.Shopify.routes.root
          ? window.Shopify.routes.root
          : '/';
        var response = await window.fetch(rootUrl + 'cart.js', {
          credentials: 'same-origin',
          headers: { Accept: 'application/json' },
        });
        if (!response.ok) return;
        var cart = await response.json();
        document.querySelectorAll('#CartCount .cart-count').forEach(function (count) {
          var value = count.querySelector('span[aria-hidden="true"]') || count.querySelector('span');
          if (value) value.textContent = String(cart.item_count);
          else count.textContent = String(cart.item_count);
          count.classList.toggle('hidden', cart.item_count < 1);
        });
      } catch (error) {
        console.warn('[VL product extra purchase option] Unable to synchronize cart count.', error);
      }
    }

    applyPurchaseState() {
      if (!this.currentPurchase || this.isApplying) return;

      var selectedOptions = this.getSelectedTargetOptions();
      var matches = this.findMatchingVariants(
        this.currentPurchase.product,
        this.currentPurchase.targetOptionNormalized,
        selectedOptions
      );
      var variant = matches.find(function (item) { return item.available; }) || matches[0];

      if (!variant) {
        this.switchToTargetProduct();
        return;
      }

      this.currentPurchase.variant = variant;
      this.isApplying = true;
      this.stopObserver();

      try {
        this.updateSelectionUI();
        this.updateNativeOptionVisibility();
        this.updateFormVariant();
        this.updatePrice();
        this.updateAffirm();
        this.updateButtons();
        this.updateQuantity();
        this.updateStickyBar();
        this.updateGallery();
        this.updateSaveLabels();
        this.refreshExtensionAvailability();
      } finally {
        this.isApplying = false;
        this.startObserver();
      }
    }

    updateSelectionUI() {
      var active = this.currentPurchase;
      this.buttons.forEach(function (button) {
        var selected = button.dataset.vlExtraPurchaseTargetOption === active.targetOptionNormalized
          && button.dataset.vlExtraPurchaseProductId === String(active.product.id);
        button.classList.toggle('selected', selected);
        button.setAttribute('aria-checked', selected ? 'true' : 'false');
        button.tabIndex = selected ? 0 : -1;
      });

      var activeGroup = this.groups.get(active.targetOptionNormalized);
      if (!activeGroup) return;
      activeGroup.inputs.forEach(function (input) {
        this.getLabelsForInput(input).forEach(function (label) {
          label.classList.remove('selected');
          label.setAttribute('aria-checked', 'false');
          label.removeAttribute('tabindex');
        });
      }, this);
    }

    updateNativeOptionVisibility() {
      var purchase = this.currentPurchase;
      var selectedOptions = this.getSelectedTargetOptions();

      this.getPickerStructure().forEach(function (optionGroup) {
        if (optionGroup.normalizedName === purchase.targetOptionNormalized) {
          this.clearVlOptionState(optionGroup);
          return;
        }

        if (!purchase.product.optionIndex.has(optionGroup.normalizedName)) {
          this.clearVlOptionState(optionGroup);
          return;
        }

        optionGroup.inputs.forEach(function (input) {
          var overrides = new Map();
          overrides.set(optionGroup.normalizedName, input.value);
          var variants = this.findMatchingVariants(
            purchase.product,
            purchase.targetOptionNormalized,
            selectedOptions,
            overrides
          );
          var exists = variants.length > 0;
          var available = variants.some(function (variant) { return variant.available; });

          this.getLabelsForInput(input).forEach(function (label) {
            label.hidden = !exists;
            label.toggleAttribute('data-vl-extra-option-hidden', !exists);
            label.classList.toggle('vl-product-extra-purchase-option__native-value--sold-out', exists && !available);
            if (exists && !available) {
              label.setAttribute('aria-disabled', 'true');
              label.setAttribute('out-of-stock', '');
              label.setAttribute('no-available', '');
            } else {
              label.removeAttribute('aria-disabled');
              label.removeAttribute('out-of-stock');
              label.removeAttribute('no-available');
            }
            label.toggleAttribute('data-vl-extra-option-state', true);
          });
        }, this);
      }, this);
    }

    clearVlOptionState(optionGroup) {
      optionGroup.inputs.forEach(function (input) {
        this.getLabelsForInput(input).forEach(function (label) {
          if (!label.hasAttribute('data-vl-extra-option-state')) return;
          label.hidden = false;
          label.removeAttribute('data-vl-extra-option-hidden');
          label.removeAttribute('data-vl-extra-option-state');
          label.classList.remove('vl-product-extra-purchase-option__native-value--sold-out');
          label.removeAttribute('out-of-stock');
          label.removeAttribute('no-available');
          label.removeAttribute('aria-disabled');
        });
      }, this);
    }

    clearAllVlOptionStates() {
      this.getPickerStructure().forEach(function (optionGroup) {
        this.clearVlOptionState(optionGroup);
      }, this);
    }

    getVariantInput() {
      return this.productForm.querySelector('[data-product-form-variant]');
    }

    updateFormVariant() {
      var input = this.getVariantInput();
      if (!input) return;
      var variantId = String(this.currentPurchase.variant.id);
      input.value = variantId;
      input.setAttribute('value', variantId);
      this.productForm.dataset.productId = String(this.currentPurchase.product.id);
      var productIdInput = this.productForm.querySelector('input[name="product-id"]');
      if (productIdInput) {
        productIdInput.value = String(this.currentPurchase.product.id);
        productIdInput.setAttribute('value', String(this.currentPurchase.product.id));
      }
      this.productForm.dataset.vlPurchaseProductId = String(this.currentPurchase.product.id);
      this.productForm.dataset.vlPurchaseVariantId = variantId;

      if (this.dispatchedPurchaseVariantId !== variantId) {
        this.dispatchedPurchaseVariantId = variantId;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    buildPriceContent() {
      var variant = this.currentPurchase.variant;
      var wrapper = document.createElement('div');
      wrapper.className = 'product-price__content';
      wrapper.setAttribute('data-product-block-item', '');

      var cost = document.createElement('div');
      cost.className = 'product-price__cost';

      var currentPrice = document.createElement('div');
      currentPrice.className = 'product-price__current-price';
      currentPrice.innerHTML = formatMoney(variant.price, this.data.moneyFormat);
      cost.appendChild(currentPrice);

      if (variant.compareAtPrice > variant.price) {
        currentPrice.classList.add('product-price__current-price--highlight-sale');
        var oldPrice = document.createElement('div');
        oldPrice.className = 'product-price__old-price product-price__old-price--highlight-sale';
        oldPrice.innerHTML = formatMoney(variant.compareAtPrice, this.data.moneyFormat);
        cost.appendChild(oldPrice);
      }

      wrapper.appendChild(cost);

      if (variant.available && variant.compareAtPrice > variant.price) {
        var badge = document.createElement('div');
        badge.className = 'product-sale-amount-badge product-sale-amount-badge--with-inner-border';
        badge.textContent = 'Save ' + htmlToText(formatMoney(
          variant.compareAtPrice - variant.price,
          this.data.moneyFormat
        ));
        wrapper.appendChild(badge);
      }

      return wrapper;
    }

    updatePrice() {
      var priceBlock = this.productRoot.querySelector(
        '#product-price-' + escapeSelector(this.dataset.productId) + '-' + escapeSelector(this.dataset.sectionId)
      );
      if (!priceBlock) return;

      var container = priceBlock.querySelector('[data-product-block-container]');
      if (!container) return;
      container.replaceChildren(this.buildPriceContent());
    }

    updateAffirmForPrice(price) {
      var amount = String(toNumber(price, 0));
      var amountChanged = false;
      this.productRoot.querySelectorAll('.affirm-as-low-as').forEach(function (message) {
        if (message.getAttribute('data-amount') === amount) return;
        message.setAttribute('data-amount', amount);
        amountChanged = true;
      });

      if (!amountChanged) return;

      var affirm = window.affirm;
      if (!affirm || !affirm.ui) return;

      var refresh = function () {
        if (typeof affirm.ui.refresh === 'function') affirm.ui.refresh();
      };

      try {
        if (typeof affirm.ui.ready === 'function') {
          affirm.ui.ready(refresh);
        } else {
          refresh();
        }
      } catch (error) {
        // The app handler also observes the variant input change. Keep the
        // updated amount even if a storefront uses a different Affirm UI API.
      }
    }

    clearAffirmUpdateTimer() {
      if (this.affirmUpdateTimer) window.clearTimeout(this.affirmUpdateTimer);
      this.affirmUpdateTimer = null;
      this.pendingAffirmPrice = null;
    }

    scheduleAffirmForPrice(price) {
      if (this.affirmUpdateTimer) window.clearTimeout(this.affirmUpdateTimer);
      this.pendingAffirmPrice = price;
      this.affirmUpdateTimer = window.setTimeout(function () {
        var pendingPrice = this.pendingAffirmPrice;
        this.affirmUpdateTimer = null;
        this.pendingAffirmPrice = null;
        if (!this.currentPurchase || pendingPrice == null) return;
        this.updateAffirmForPrice(pendingPrice);
      }.bind(this), AFFIRM_UPDATE_DELAY);
    }

    updateAffirm() {
      if (!this.currentPurchase) return;
      this.scheduleAffirmForPrice(this.currentPurchase.variant.price);
    }

    getPurchaseButtons() {
      return Array.from(this.productForm.querySelectorAll('button[type="submit"][name="add"]'));
    }

    updateButtons() {
      var available = Boolean(this.currentPurchase.variant.available);
      var soldOutText = window.auroraThemeLocales
        && window.auroraThemeLocales.variantStrings
        && window.auroraThemeLocales.variantStrings.soldOut;
      var addText = window.auroraThemeLocales
        && window.auroraThemeLocales.variantStrings
        && window.auroraThemeLocales.variantStrings.addToCart;

      this.getPurchaseButtons().forEach(function (button) {
        button.disabled = !available;
        var text = button.querySelector('[data-button-text]');
        if (text) {
          if (!text.dataset.vlOriginalText) text.dataset.vlOriginalText = text.textContent.trim();
          text.textContent = available ? (addText || text.dataset.vlOriginalText) : (soldOutText || 'Sold out');
        }
      });
    }

    updateQuantity() {
      var input = this.productForm.querySelector('input[name="quantity"]');
      if (!input) return;
      var rule = this.currentPurchase.variant.quantityRule || {};
      input.min = String(rule.min || 1);
      input.step = String(rule.increment || 1);
      input.max = rule.max == null ? '9999' : String(rule.max);
      if (Number(input.value) < Number(input.min)) input.value = input.min;
      if (Number(input.value) > Number(input.max)) input.value = input.max;
    }

    getCurrentImage() {
      var variantImage = this.currentPurchase.variant.featuredImage || {};
      var productImage = this.currentPurchase.product.featuredImage || {};
      return variantImage.url ? variantImage : productImage;
    }

    updateStickyBar() {
      var sticky = this.productForm.querySelector('.sticky-atc__inner');
      if (!sticky) return;

      var title = sticky.querySelector('.sticky-atc__title-name');
      if (title) title.textContent = this.currentPurchase.product.title;

      var price = sticky.querySelector('[data-sticky-price]');
      if (price) {
        price.dataset.price = String(this.currentPurchase.variant.price);
        price.dataset.accPrice = '0';
        price.innerHTML = formatMoney(this.currentPurchase.variant.price, this.data.moneyFormat);
      }

      var image = this.getCurrentImage();
      var img = sticky.querySelector('.sticky-atc__image img');
      if (img && image.url) {
        img.src = image.url;
        img.srcset = '';
        img.alt = image.alt || this.currentPurchase.product.title;
      }
    }

    getProductContent() {
      return this.closest('product-content')
        || (this.productRoot && this.productRoot.querySelector('product-content'));
    }

    getPurchaseMediaSources(productId) {
      var template = this.querySelector(
        '[data-vl-extra-purchase-media-template="' + escapeSelector(productId) + '"]'
      );
      if (!template || !template.content) return null;

      var desktop = template.content.querySelector('[data-product-desktop-media]');
      var mobile = template.content.querySelector('[data-product-mobile-media]');
      var modalTemplate = template.content.querySelector('product-modal template[data-template="modal"]');

      return {
        desktop: desktop,
        mobile: mobile,
        modal: modalTemplate && modalTemplate.content
          ? modalTemplate.content.querySelector('[data-container="modal"]')
          : null,
      };
    }

    getFilteredMediaNodes(container, variant) {
      if (!container) return [];
      var nodes = Array.from(container.children);
      var variantId = String(variant.id);
      var withoutAds = nodes.filter(function (node) {
        return !String(node.dataset.variant || '').toLocaleLowerCase().includes('ads');
      });
      var matched = withoutAds.filter(function (node) {
        return String(node.dataset.variant || '').includes(variantId);
      });

      if (matched.length > 0) return matched;

      // Some existing purchase products still only have legacy `_ads` media.
      // Keep the selected variant usable until those alts are migrated to numeric
      // `variantID:` values, while never falling back to every colour at once.
      var featuredMediaId = variant.featuredImage && variant.featuredImage.mediaId;
      var featured = nodes.find(function (node) {
        return featuredMediaId != null && String(node.dataset.mediaId) === String(featuredMediaId);
      });
      if (featured) return [featured];
      if (withoutAds.length > 0) return withoutAds;
      return nodes.length > 0 ? [nodes[0]] : [];
    }

    prepareMediaNodes(container, variant, type) {
      var targetModalId = '#ProductModal-' + this.dataset.productId + '-' + this.dataset.sectionId;
      var nodes = this.getFilteredMediaNodes(container, variant).map(function (node) {
        return node.cloneNode(true);
      });

      nodes.forEach(function (node, index) {
        node.classList.remove('is-selected', 'is-primary', 'is-in-view', 'is-snapped', 'is-prev-prev');
        if (type === 'carousel' && index === 0) node.classList.add('is-selected');
        if (type === 'dots') node.setAttribute('data-dot-index', String(index));
        node.querySelectorAll('[data-modal]').forEach(function (button) {
          button.setAttribute('data-modal', targetModalId);
        });
      });

      if (type === 'carousel' && nodes[0]) {
        nodes[0].classList.toggle('product-media-carousel-mobile__slide--alone', nodes.length < 2);
      }
      return nodes;
    }

    getSourceContainer(sourceRoot, type) {
      if (!sourceRoot) return null;
      if (sourceRoot.dataset && sourceRoot.dataset.container === type) return sourceRoot;
      return sourceRoot.querySelector('[data-container="' + type + '"]');
    }

    snapshotGalleryContainer(container) {
      if (!container || this.gallerySnapshots.has(container)) return;
      this.gallerySnapshots.set(container, Array.from(container.children).map(function (child) {
        return child.cloneNode(true);
      }));
    }

    replaceGalleryContainer(container, sourceRoot, variant) {
      if (!container) return 0;
      var type = container.dataset.container;
      var source = this.getSourceContainer(sourceRoot, type);
      if (!source) return 0;
      this.snapshotGalleryContainer(container);
      var nodes = this.prepareMediaNodes(source, variant, type);
      if (nodes.length === 0) return 0;
      container.replaceChildren.apply(container, nodes);
      return nodes.length;
    }

    reinitializeGallery(productContent) {
      var scope = productContent || this.productRoot;
      var carousels = productContent && Array.isArray(productContent.carousels)
        ? productContent.carousels
        : Array.from(scope.querySelectorAll('carousel-component, carousel-dots'));

      carousels.forEach(function (carousel) {
        if (typeof carousel.reInit === 'function') carousel.reInit();
        if (carousel.embla && typeof carousel.embla.scrollTo === 'function') {
          carousel.embla.scrollTo(0, true);
        }
      });

      scope.querySelectorAll('[data-product-media-container]').forEach(function (mediaScope) {
        var visibleContainer = mediaScope.querySelector('[data-container="carousel"], [data-container="grid"]');
        var total = visibleContainer ? visibleContainer.children.length : 0;
        mediaScope.querySelectorAll('.media-counter-current').forEach(function (counter) {
          counter.textContent = total > 0 ? '1' : '0';
        });
        mediaScope.querySelectorAll('.media-counter-total').forEach(function (counter) {
          counter.textContent = String(total);
        });
      });

      if (productContent && productContent.modal) {
        if (productContent.modalQuantityElement) {
          var modalContainer = productContent.modal.element
            && productContent.modal.element.querySelector('[data-container="modal"]');
          productContent.modalQuantityElement.textContent = String(
            modalContainer ? modalContainer.children.length : 0
          );
        }
        if (typeof productContent.modal.updateModal === 'function') {
          productContent.modal.updateModal();
        }
      }
    }

    updateGallery() {
      var purchase = this.currentPurchase;
      var galleryKey = String(purchase.product.id) + ':' + String(purchase.variant.id);
      if (this.appliedGalleryKey === galleryKey) return;

      var sources = this.getPurchaseMediaSources(purchase.product.id);
      if (!sources) return;

      var productContent = this.getProductContent();
      var desktopRoot = productContent && productContent.desktopContent
        ? productContent.desktopContent
        : this.productRoot.querySelector('[data-product-desktop-media]');
      var mobileRoot = productContent && productContent.mobileContent
        ? productContent.mobileContent
        : this.productRoot.querySelector('[data-product-mobile-media]');

      var desktopContainers = desktopRoot
        ? Array.from(desktopRoot.querySelectorAll('[data-container]'))
        : [];
      var mobileContainers = mobileRoot
        ? Array.from(mobileRoot.querySelectorAll('[data-container]'))
        : [];

      var replacedContainers = 0;
      desktopContainers.forEach(function (container) {
        if (this.replaceGalleryContainer(container, sources.desktop, purchase.variant) > 0) {
          replacedContainers += 1;
        }
      }, this);
      mobileContainers.forEach(function (container) {
        if (this.replaceGalleryContainer(container, sources.mobile, purchase.variant) > 0) {
          replacedContainers += 1;
        }
      }, this);

      var modalContainer = productContent && productContent.modal && productContent.modal.element
        ? productContent.modal.element.querySelector('[data-container="modal"]')
        : null;
      if (modalContainer) {
        if (this.replaceGalleryContainer(modalContainer, sources.modal, purchase.variant) > 0) {
          replacedContainers += 1;
        }
      }

      if (replacedContainers === 0) return;

      if (productContent) {
        var currentRoot = productContent.isMobile ? sources.mobile : sources.desktop;
        var currentSource = this.getSourceContainer(currentRoot, 'carousel')
          || this.getSourceContainer(currentRoot, 'grid');
        productContent.currentImagesQuantity = this.getFilteredMediaNodes(
          currentSource,
          purchase.variant
        ).length;
      }
      this.reinitializeGallery(productContent);
      this.appliedGalleryKey = galleryKey;
    }

    restoreProductContentGallery(productContent) {
      if (!productContent || !this.variantPicker.variant) return false;
      var required = [
        'replaceCarouselSlides',
        'replaceDotsSlides',
        'replaceGridSlides',
        'replaceModalSlides',
        'reInitCarousels',
        'setCurrentContainers',
      ];
      if (!required.every(function (name) { return typeof productContent[name] === 'function'; })) {
        return false;
      }

      [
        [productContent.desktopContainers, productContent.desktopImages],
        [productContent.mobileContainers, productContent.mobileImages],
      ].forEach(function (state) {
        productContent.currentContainers = state[0];
        productContent.currentImages = state[1];
        state[0].forEach(function (container) {
          switch (container.dataset.container) {
            case 'carousel': productContent.replaceCarouselSlides(container); break;
            case 'dots': productContent.replaceDotsSlides(container); break;
            case 'grid': productContent.replaceGridSlides(container); break;
            case 'modal': productContent.replaceModalSlides(container); break;
          }
        });
      });

      productContent.setCurrentContainers();
      if (typeof productContent.scrollToVariant === 'function') {
        productContent.scrollToVariant(this.variantPicker.variant);
      }
      this.reinitializeGallery(productContent);
      return true;
    }

    restoreGallery() {
      this.appliedGalleryKey = null;
      var productContent = this.getProductContent();
      var restoredFromSnapshots = false;

      this.gallerySnapshots.forEach(function (children, container) {
        if (container.isConnected) {
          container.replaceChildren.apply(container, children.map(function (child) {
            return child.cloneNode(true);
          }));
          restoredFromSnapshots = true;
        }
      });

      if (restoredFromSnapshots && productContent && typeof productContent.assignImages === 'function') {
        productContent.desktopImages = productContent.assignImages(productContent.desktopContainers);
        productContent.mobileImages = productContent.assignImages(productContent.mobileContainers);
        if (typeof productContent.setCurrentContainers === 'function') {
          productContent.setCurrentContainers();
        }
        this.reinitializeGallery(productContent);
      } else {
        this.restoreProductContentGallery(productContent);
      }
      this.gallerySnapshots.clear();
    }

    updateSaveLabelsForVariant(variant, productId) {
      var saved = Math.max(
        toNumber(variant.compareAtPrice != null ? variant.compareAtPrice : variant.compare_at_price, 0)
          - toNumber(variant.price, 0),
        0
      );
      var labelProductId = productId == null ? this.dataset.productId : productId;
      var selector = '[data-image-price-label][data-product-id="' + escapeSelector(labelProductId) + '"]';
      this.productRoot.querySelectorAll(selector).forEach(function (label) {
        label.hidden = saved <= 0;
        label.textContent = saved > 0 ? 'Save ' + formatCompactMoney(saved, this.data.moneyFormat) : '';
      }, this);
    }

    updateSaveLabels() {
      this.updateSaveLabelsForVariant(
        this.currentPurchase.variant,
        this.currentPurchase.product.id
      );
    }

    getTargetVariantImage(variant) {
      var featuredImage = variant.featured_image || variant.featuredImage || {};
      if (typeof featuredImage === 'string') {
        return { url: featuredImage, alt: this.data.targetProduct.title };
      }

      var previewImage = featuredImage.preview_image || featuredImage.previewImage || {};
      var fallback = this.data.targetProduct.featuredImage || {};
      return {
        url: featuredImage.url || featuredImage.src || previewImage.url || previewImage.src || fallback.url,
        alt: featuredImage.alt || previewImage.alt || fallback.alt || this.data.targetProduct.title,
      };
    }

    restoreTargetPurchasePresentation() {
      var variant = this.variantPicker.variant;
      if (!variant) return;

      var sticky = this.productForm.querySelector('.sticky-atc__inner');
      if (sticky) {
        var title = sticky.querySelector('.sticky-atc__title-name');
        if (title) {
          title.textContent = String(this.data.targetProduct.id) === '15010086617460'
            ? (variant.options && variant.options[0]) || this.data.targetProduct.title
            : this.data.targetProduct.title;
        }

        var price = sticky.querySelector('[data-sticky-price]');
        if (price) {
          price.dataset.price = String(variant.price);
          price.dataset.accPrice = '0';
          price.innerHTML = formatMoney(variant.price, this.data.moneyFormat);
        }

        var image = this.getTargetVariantImage(variant);
        var img = sticky.querySelector('.sticky-atc__image img');
        if (img && image.url) {
          img.src = image.url;
          img.srcset = '';
          img.alt = image.alt;
        }
      }

      var available = Boolean(variant.available);
      var soldOutText = window.auroraThemeLocales
        && window.auroraThemeLocales.variantStrings
        && window.auroraThemeLocales.variantStrings.soldOut;
      var addText = window.auroraThemeLocales
        && window.auroraThemeLocales.variantStrings
        && window.auroraThemeLocales.variantStrings.addToCart;
      this.getPurchaseButtons().forEach(function (button) {
        button.disabled = !available;
        var text = button.querySelector('[data-button-text]');
        if (!text) return;
        if (!text.dataset.vlOriginalText) text.dataset.vlOriginalText = text.textContent.trim();
        text.textContent = available ? (addText || text.dataset.vlOriginalText) : (soldOutText || 'Sold out');
      });

      var quantity = this.productForm.querySelector('input[name="quantity"]');
      var rule = variant.quantity_rule || variant.quantityRule || {};
      if (quantity) {
        quantity.min = String(rule.min || 1);
        quantity.step = String(rule.increment || 1);
        quantity.max = rule.max == null ? '9999' : String(rule.max);
        if (Number(quantity.value) < Number(quantity.min)) quantity.value = quantity.min;
        if (Number(quantity.value) > Number(quantity.max)) quantity.value = quantity.max;
      }

      this.updateSaveLabelsForVariant(variant);
      this.clearAffirmUpdateTimer();
      this.updateAffirmForPrice(variant.price);
    }

    switchToTargetProduct() {
      if (!this.currentPurchase) return;
      this.clearApplyTimers();
      this.clearAffirmUpdateTimer();
      this.stopObserver();
      this.isApplying = true;

      try {
        this.currentPurchase = null;
        this.buttons.forEach(function (button) {
          button.classList.remove('selected');
          button.setAttribute('aria-checked', 'false');
          button.tabIndex = -1;
        });
        this.clearAllVlOptionStates();
        this.restoreGallery();
        this.productForm.dataset.productId = String(this.data.targetProduct.id);
        var productIdInput = this.productForm.querySelector('input[name="product-id"]');
        if (productIdInput) {
          productIdInput.value = String(this.data.targetProduct.id);
          productIdInput.setAttribute('value', String(this.data.targetProduct.id));
        }
        delete this.productForm.dataset.vlPurchaseProductId;
        delete this.productForm.dataset.vlPurchaseVariantId;
        this.dispatchedPurchaseVariantId = null;

        if (typeof this.variantPicker.updateLabels === 'function') this.variantPicker.updateLabels();
        if (typeof this.variantPicker.setOptionsAvailable === 'function') this.variantPicker.setOptionsAvailable();
        if (typeof this.variantPicker.updateVariantInput === 'function' && this.variantPicker.variant) {
          this.variantPicker.updateVariantInput();
        }
        if (typeof this.variantPicker.updateBlocks === 'function' && this.variantPicker.variant) {
          this.variantPicker.updateBlocks();
        }
        if (typeof this.variantPicker.setFirstMedia === 'function' && this.variantPicker.variant) {
          this.variantPicker.setFirstMedia(false);
        }
        this.restoreTargetPurchasePresentation();
      } finally {
        this.isApplying = false;
        this.startObserver();
      }

      this.refreshExtensionAvailability();
      this.dispatchPurchaseSubjectChange();
      this.scheduleTargetGalleryRestore();
    }

    dispatchPurchaseSubjectChange() {
      this.updateTargetProductExtrasVisibility();
      var detail = this.currentPurchase
        ? {
            source: 'purchase',
            productId: this.currentPurchase.product.id,
            variantId: this.currentPurchase.variant.id,
            product: this.currentPurchase.product,
            variant: this.currentPurchase.variant,
            selectedTargetOptions: Object.fromEntries(this.getSelectedTargetOptions()),
          }
        : {
            source: 'target',
            productId: Number(this.dataset.productId),
            variantId: this.variantPicker && this.variantPicker.variant ? this.variantPicker.variant.id : null,
            selectedTargetOptions: Object.fromEntries(this.getSelectedTargetOptions()),
          };

      this.dispatchEvent(new CustomEvent('vl:purchase-subject-change', {
        bubbles: true,
        detail: detail,
      }));
    }

    updateTargetProductExtrasVisibility() {
      if (!this.productRoot) return;
      var isPurchaseProduct = Boolean(this.currentPurchase);
      this.productRoot.querySelectorAll('.bundle-container #free').forEach(function (freeItemsSection) {
        freeItemsSection.hidden = isPurchaseProduct;
        freeItemsSection.setAttribute('aria-hidden', isPurchaseProduct ? 'true' : 'false');
      });
    }

    isAffirmMutation(mutation) {
      var isAffirmNode = function (node) {
        if (!node || node.nodeType !== 1) return false;
        return node.matches('.affirm-as-low-as') || Boolean(node.closest('.affirm-as-low-as'));
      };

      if (isAffirmNode(mutation.target)) return true;

      var changedNodes = Array.from(mutation.addedNodes || []).concat(
        Array.from(mutation.removedNodes || [])
      );
      return changedNodes.length > 0 && changedNodes.every(isAffirmNode);
    }

    handleMutations(mutations) {
      if (!this.currentPurchase || this.isApplying) return;
      var hasAffirmMutation = false;
      var hasProductMutation = false;

      (mutations || []).forEach(function (mutation) {
        if (this.isAffirmMutation(mutation)) hasAffirmMutation = true;
        else hasProductMutation = true;
      }, this);

      if (hasProductMutation) {
        this.schedulePurchaseState();
      } else if (hasAffirmMutation) {
        this.updateAffirm();
      }
    }

    startObserver() {
      if (!this.currentPurchase || this.observer) return;
      this.observerTarget = this.productRoot.querySelector('[id^="ProductInfoWrapper-"]') || this.productRoot;
      this.observer = new MutationObserver(this.handleMutations);
      this.observer.observe(this.observerTarget, {
        attributes: true,
        attributeFilter: ['data-amount'],
        childList: true,
        subtree: true,
      });
    }

    stopObserver() {
      if (!this.observer) return;
      this.observer.disconnect();
      this.observer = null;
      this.observerTarget = null;
    }
  }

  if (!customElements.get(COMPONENT_NAME)) {
    customElements.define(COMPONENT_NAME, VlProductExtraPurchaseOption);
  }
})();
